import { getAge } from './utils'
import { ChildCompetencyLevel, type ChildCompetencyPolicy } from '../../models/user/User'
import { evaluateConditions } from '../conditionalEngine/conditionalEngine'

export type ChildCompetencyPolicyEvaluation = {
  competencyLevel: ChildCompetencyLevel
  /** Inclusive lower bound of the matched band. */
  minimumAge: number
  /** Inclusive upper bound of the matched band. Null means unbounded above. */
  maximumAge: number | null
}

const CHILD_COMPETENCY_LEVELS: ReadonlySet<unknown> = new Set(Object.values(ChildCompetencyLevel))

const isChildCompetencyLevel = (value: unknown): value is ChildCompetencyLevel => CHILD_COMPETENCY_LEVELS.has(value)

export const evaluateChildCompetencyPolicy = (
  policy: ChildCompetencyPolicy | null | undefined,
  age: number,
): ChildCompetencyPolicyEvaluation | null => {
  if (policy?.conditions == null || !Number.isInteger(age) || age < 0) {
    return null
  }

  try {
    const [isSatisfied, { competency, min_age: minimumAge, max_age: maximumAge }] = evaluateConditions(
      policy.conditions,
      { age },
    )
    if (
      !isSatisfied ||
      !isChildCompetencyLevel(competency) ||
      typeof minimumAge !== 'number' ||
      (maximumAge !== null && typeof maximumAge !== 'number')
    ) {
      return null
    }
    return { competencyLevel: competency, minimumAge, maximumAge }
  } catch {
    // The engine throws on malformed conditions, so an unreadable policy fails closed
    return null
  }
}

export const isChildFullyCompetent = (
  dob: string,
  dateFormat: string,
  policy: ChildCompetencyPolicy | null | undefined,
  isOUSChildTriageEnabled: boolean,
): boolean => {
  const age = getAge({ date: dob, dateFormat })
  if (isOUSChildTriageEnabled && policy) {
    const result = evaluateChildCompetencyPolicy(policy, age)
    if (result) {
      return result.competencyLevel === ChildCompetencyLevel.FULL_COMPETENCY
    }
  }
  return age >= 18
}
